// classes/index.js
// Central export point for all soldier classes

import { SniperClass } from './SniperClass';
import { RangerClass } from './RangerClass';
import { SupportClass } from './SupportClass';
import { HeavyClass } from './HeavyClass';

// Export all classes
export { SniperClass, RangerClass, SupportClass, HeavyClass };

// Create a lookup object for easy access
export const SOLDIER_CLASSES = {
  sniper: SniperClass,
  ranger: RangerClass,
  support: SupportClass,
  heavy: HeavyClass
};

// Helper function to get a class by ID
export const getClassById = (classId) => {
  return SOLDIER_CLASSES[classId] || null;
};

// Helper function to get all available classes as array
export const getAllClasses = () => {
  return Object.values(SOLDIER_CLASSES);
};

// Helper function to calculate total XP cost for a skill
export const getSkillXPCost = (classId, skillId) => {
  const soldierClass = SOLDIER_CLASSES[classId];
  if (!soldierClass) return 0;
  
  const skill = soldierClass.skills.find(s => s.id === skillId);
  return skill ? skill.xpRequired : 0;
};

// Helper function to get all skills available at a certain XP level
export const getAvailableSkills = (classId, currentXP) => {
  const soldierClass = SOLDIER_CLASSES[classId];
  if (!soldierClass) return [];
  
  return soldierClass.skills.filter(skill => skill.xpRequired <= currentXP);
};

// Helper function to get next skill to unlock
export const getNextSkill = (classId, currentXP, unlockedSkillIds) => {
  const soldierClass = SOLDIER_CLASSES[classId];
  if (!soldierClass) return null;
  
  // Find skills that:
  // 1. Require more XP than current XP
  // 2. Are not already unlocked
  // Sort by XP requirement and return the first one
  const nextSkills = soldierClass.skills
    .filter(skill => 
      skill.xpRequired > currentXP && 
      !unlockedSkillIds.includes(skill.id)
    )
    .sort((a, b) => a.xpRequired - b.xpRequired);
  
  return nextSkills.length > 0 ? nextSkills[0] : null;
};

// Helper function to get all abilities unlocked at a certain XP level
export const getUnlockedAbilities = (classId, currentXP) => {
  const soldierClass = SOLDIER_CLASSES[classId];
  if (!soldierClass) return [];
  
  return soldierClass.abilities.filter(ability => ability.unlockAtXP <= currentXP);
};

// Helper function to check if soldier can unlock a skill
export const canUnlockSkill = (classId, skillId, currentXP, unlockedSkillIds) => {
  const soldierClass = SOLDIER_CLASSES[classId];
  if (!soldierClass) return false;
  
  const skill = soldierClass.skills.find(s => s.id === skillId);
  if (!skill) return false;
  
  // Check if already unlocked
  if (unlockedSkillIds.includes(skillId)) return false;
  
  // Check if has enough XP
  if (currentXP < skill.xpRequired) return false;
  
  return true;
};

// Helper function to calculate soldier's total stats with all unlocked skills
export const calculateTotalStats = (classId, unlockedSkillIds) => {
  const soldierClass = SOLDIER_CLASSES[classId];
  if (!soldierClass) return null;
  
  // Start with base stats
  const totalStats = { ...soldierClass.baseStats };
  
  // Apply effects from all unlocked skills
  unlockedSkillIds.forEach(skillId => {
    const skill = soldierClass.skills.find(s => s.id === skillId);
    if (skill && skill.effect) {
      Object.keys(skill.effect).forEach(stat => {
        if (typeof skill.effect[stat] === 'number' && totalStats[stat] !== undefined) {
          totalStats[stat] += skill.effect[stat];
        }
      });
    }
  });
  
  return totalStats;
};

// Helper to get skills by tier
export const getSkillsByTier = (classId, tier) => {
  const soldierClass = SOLDIER_CLASSES[classId];
  if (!soldierClass) return [];
  
  return soldierClass.skills.filter(skill => skill.tier === tier);
};